import { TZDateMini } from '@date-fns/tz';
import { isValid, startOfDay } from 'date-fns';
import { useReducer } from 'react';

import { getTimezone } from '@dynatrace-sdk/user-preferences';

import { useId } from '../../../core/hooks/useId.js';
import { isTimeValue } from '../../../core/time/helpers.js';
import { useCalendarStore } from '../../../core/time/timeframeStore/useTimeframeStore.js';
import type {
  CalendarProps,
  CalendarStrictValueType,
} from '../Calendar.types.js';
import { initialState as defaultState } from '../contexts/CalendarContext.js';
import { reducer } from '../state/calendar-state-reducer.js';
import { type CalendarType } from '../types/calendarConfiguration.js';
import type { CalendarState } from '../types/shared-types.js';

//#region types
/** @internal */
export type UseCalendarProps<Type extends CalendarType> = Pick<
  CalendarProps<Type>,
  'onChange' | 'type' | 'precision'
> & {
  value: CalendarStrictValueType<Type>;
};
//#endregion

/**
 * Calendar hook to initialize the calendar state and update it to prop changes.
 * @internal
 */
export function useCalendar<Type extends CalendarType>({
  // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error, @typescript-eslint/ban-ts-comment
  // @ts-ignore Strict mode transition - needs to be investigated
  type = 'single',
}: UseCalendarProps<Type>) {
  const uniqueId = useId();
  // All parse times here need to use the same _now_ date, to not be subject
  // to millisecond differences during parsing.
  const configuredTimezone = getTimezone();

  const calendarValue = useCalendarStore((state) => state.calendarValue);

  const activeISODate = isTimeValue(calendarValue)
    ? calendarValue?.absoluteDate
    : calendarValue?.from?.absoluteDate;

  const today = startOfDay(TZDateMini.tz(configuredTimezone));

  const parsedActiveDate = new TZDateMini(
    activeISODate ?? today.toISOString(),
    configuredTimezone,
  );

  const initialState: CalendarState = {
    ...defaultState,
    type,
    activeDate: isValid(parsedActiveDate) ? parsedActiveDate : today,
  };

  const [state, dispatch] = useReducer(
    reducer,
    // activeDate might be undefined but will be set in the initializer, to ensure the correct timezone.
    initialState,
    (state: CalendarState) => {
      // Make sure the state initializes with a new TZDateMini when the component is created
      // instead of on the module scope.
      // Extract the user-settings timezone to use for the calculation

      return {
        ...state,
        activeDate: state.activeDate ?? today,
        today,
      };
    },
  );

  return { state, dispatch, uniqueId };
}
